/**
 * UserPushOptInBanner — iter-169r. One-time dismissable banner that
 * surfaces on the user dashboard. Triggers `Notification.requestPermission`
 * (which MUST be a user-gesture) and registers the subscription with
 * `/api/push/subscribe`. Once handled, persists the decision in
 * localStorage so we never nag again.
 *
 * Decision keys:
 *   • `lumfi.user_push.decision` = "subscribed" | "dismissed" | "denied"
 *
 * Browser-side guards:
 *   - Hidden when permission is already "granted" (assume opted in).
 *   - Hidden when permission is "denied" (user blocked it — we'd just
 *     fail anyway).
 *   - Hidden when push isn't supported (Safari iOS <16.4, etc.).
 */
import { useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { tx } from "../../i18n";
import {
  enableUserPushNotifications,
  getSupportState,
} from "../../services/userPushService";

const STORAGE_KEY = "lumfi.user_push.decision";

function readDecision() {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function writeDecision(v) {
  try { localStorage.setItem(STORAGE_KEY, v); } catch { /* noop */ }
}

export function UserPushOptInBanner({ lang = "es" }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const support = getSupportState();
    if (!support.supported) return;
    if (support.permission === "granted" || support.permission === "denied") {
      // Either already opted in or hard-blocked — no banner needed.
      return;
    }
    const decision = readDecision();
    if (decision) return;        // user already chose
    // Show after a short delay so it doesn't fight the page render.
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enableUserPushNotifications();
      writeDecision("subscribed");
      toast.success(tx(lang, "Done! We'll notify you here.", "¡Listo! Te avisaremos por acá.", "Pronto! Vamos te notificar por aqui."));
      setVisible(false);
    } catch (e) {
      writeDecision("denied");
      toast.error(e.message || tx(lang, "Couldn't enable", "No se pudo activar", "Não foi possível ativar"));
      setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    writeDecision("dismissed");
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <Card
      className="p-4 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white relative"
      data-testid="user-push-optin-banner"
    >
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
        data-testid="user-push-dismiss"
        aria-label={tx(lang, "Close", "Cerrar", "Fechar")}
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="rounded-full bg-emerald-100 p-2 shrink-0">
          <Bell className="h-4 w-4 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">
            {tx(lang, "Enable notifications?", "¿Activar notificaciones?", "Ativar notificações?")}
          </h3>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            {tx(lang,
              "We'll ping your browser when a payment is approved, when a client pays an invoice, or when your subscription is about to expire. No spam.",
              "Te avisamos en tu navegador cuando se aprueba un pago, cuando un cliente paga una factura pendiente, o cuando tu suscripción está por vencer. Sin spam.",
              "Avisamos no seu navegador quando um pagamento for aprovado, quando um cliente pagar uma fatura pendente, ou quando sua assinatura estiver para vencer. Sem spam.",
            )}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs"
              onClick={handleEnable}
              disabled={busy}
              data-testid="user-push-enable"
            >
              <Bell className="h-3 w-3" />
              {busy
                ? tx(lang, "Enabling…", "Activando…", "Ativando…")
                : tx(lang, "Enable notifications", "Activar notificaciones", "Ativar notificações")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-slate-500 hover:text-slate-700"
              onClick={handleDismiss}
              data-testid="user-push-later"
            >
              {tx(lang, "Later", "Más tarde", "Mais tarde")}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
